// Token Duration Options
export type TokenDuration =
  | 60
  | 360
  | 720
  | 1440
  | 10080
  | 43200

// Login Request
export interface LoginRequest {
  email: string
  password: string
  sessionName?: string
  tokenDuration?: TokenDuration
}

// Login Response
export interface LoginResponse {
  success: boolean
  token?: string
  refreshToken?: string
  user?: UserProfileDto
  expiresAt?: string
  refreshTokenExpiresAt?: string
  mfaRequired?: boolean
  mfaToken?: string
  message?: string
  /** Staff account signed in without two-factor: staff roles are left out of this session. */
  staffMfaRequired?: boolean
}

// Refresh Token Request
export interface RefreshTokenRequest {
  refreshToken: string
}

// Refresh Token Response
export interface RefreshTokenResponse {
  accessToken: string
  refreshToken: string
  expiresAt: string
  refreshTokenExpiresAt: string
  user: UserProfileDto
  staffMfaRequired?: boolean
}

// Session DTO
export interface SessionDto {
  id: string
  sessionName?: string
  ipAddress: string
  deviceInfo: string
  browserInfo: string
  createdAt: string
  lastAccessedAt: string
  expiresAt: string
  isCurrentSession: boolean
}

// Update Session Name Request
export interface UpdateSessionNameRequest {
  sessionName: string
}

// Token Duration Setting Request
export interface TokenDurationSettingRequest {
  tokenDuration: TokenDuration
}

// TOTP MFA Verification
export interface MfaVerificationRequest {
  mfaToken: string
  code: string
  backupCode?: string
}

// Token Validation Response
export interface ValidateTokenResponse {
  isValid: boolean
  errorMessage?: string
}

// User Flags (bitwise permissions)
export enum UserFlags {
  None = 0,
  CanCreateSilentAdventures = 1,
  // Future flags: 2, 4, 8, 16, 32, 64, 128...
}

// User Profile DTO
export interface UserProfileDto {
  userId: string
  id?: string // Alias for userId for compatibility
  email: string
  firstName: string
  lastName: string
  displayName: string
  userName?: string // Username for display
  username?: string // Alternative casing
  avatarUrl?: string
  avatar?: string // Alternative field name
  roles: string[]
  /** Holds a staff role but this session skipped two-factor, so `roles` carries no staff role. */
  staffMfaRequired?: boolean
  isActive: boolean
  lastLoginDate?: string
  homeLatitude?: number
  homeLongitude?: number
  hometown?: string
  userFlags?: number // Bitwise flags - only visible to the user themselves
  // Subscription fields
  isPaidSubscription?: boolean
  subscriptionPlan?: string
  subscriptionPlanDisplayName?: string
  subscriptionStatus?: string
  // Profile fields
  phoneNumber?: string
  isEmailVerified?: boolean
  isPhoneVerified?: boolean
  billingAddress?: {
    street?: string
    city?: string
    state?: string
    zipCode?: string
    country?: string
  }
}

// Phone Verification - Step 1: Start Phone Verification
export interface StartPhoneVerificationRequest {
  phoneNumber: string
  shareCode?: string
}

export interface StartPhoneVerificationResponse {
  success: boolean
  sessionId: string
  message: string
}

// Phone Verification - Step 2: Verify Phone Code
export interface VerifyPhoneRequest {
  phoneNumber: string
  code: string
  sessionId: string
}

export interface VerifyPhoneResponse {
  success: boolean
  verifiedPhoneToken: string
  message: string
}

// Phone Verification - Step 3: Complete Registration
export interface CompleteRegistrationRequest {
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword: string
  verifiedPhoneToken: string
}

export interface CompleteRegistrationResponse {
  success: boolean
  message: string
  token: string
  expiresAt: string
}

// Password Reset - Step 1: Request Password Reset
export interface ForgotPasswordRequest {
  email?: string
  phoneNumber?: string
  method: 'email' | 'sms'
}

export interface ForgotPasswordResponse {
  success: boolean
  message: string
}

// Password Reset - Step 2: Verify SMS Code (SMS flow only)
export interface VerifyResetCodeRequest {
  phoneNumber: string
  code: string
}

export interface VerifyResetCodeResponse {
  success: boolean
  token: string
  message: string
}

// Password Reset - Step 3: Reset Password
export interface ResetPasswordRequest {
  identifier: string // email or phone number
  token: string
  newPassword: string
}

export interface ResetPasswordResponse {
  success: boolean
  message: string
  sessionsTerminated?: number
}

// OAuth Provider
export interface OAuthProvider {
  name: string
  isEnabled: boolean
}

export interface OAuthProvidersResponse {
  providers: OAuthProvider[]
}
